import { Box as ChakraBox, Popover, PopoverArrow, PopoverContent, PopoverTrigger, Portal } from "@chakra-ui/react";
import { Text } from "@familyzone/component-library";
import { ChipPopoverProps } from "@familyzone/component-library/dist/components/Selectors/MultiSearchSelector";
import { MultiOption } from "@familyzone/component-library/dist/components/Selectors/components/SelectorOption";
import React from "react";
import Link from "../components/Link";
import { GroupOption, GroupUMS } from "../types/Groups";
import { UserOption, UserUMS } from "../types/Users";

type PopoverType = "user" | "group";

interface PopoverConfig {
  type: PopoverType;
  displayName: string;
  linkUrl: string;
  label: string;
}

const POPOVER_Z_INDEX = 9999999;
const POPOVER_GUTTER = 2;
const POPOVER_OFFSET: [number, number] = [0, -2];
const POPOVER_PADDING = 8;

const ROUTES = {
  USERS: "/config/device/userdb/users",
  GROUPS: "/config/device/userdb/groups",
} as const;

const LABELS = {
  USERNAME: "Username",
  GROUP_NAME: "Group Name",
} as const;

/**
 * Generates popover configuration based on type and display name
 */
const getPopoverConfig = (type: PopoverType, displayName: string): PopoverConfig => {
  if (type === "user") {
    return {
      type: "user",
      displayName,
      linkUrl: `${ROUTES.USERS}?username=${encodeURIComponent(displayName)}`,
      label: LABELS.USERNAME,
    };
  }

  return {
    type: "group",
    displayName,
    linkUrl: `${ROUTES.GROUPS}?groupname=${encodeURIComponent(displayName)}`,
    label: LABELS.GROUP_NAME,
  };
};

/**
 * Extracts display name from user option, falling back to search results if needed
 */
const extractUserDisplayName = (userOption: UserOption, userResults: UserUMS[]): string => {
  const username = "value" in userOption && typeof userOption.value === "string" ? userOption.value : userOption.username;

  if (!userOption.firstName && !userOption.email && username) {
    const foundUser = userResults.find((user) => user.username === username);
    return foundUser?.username || username;
  }

  return userOption.username || ("value" in userOption && typeof userOption.value === "string" ? userOption.value : "");
};

/**
 * Extracts display name from group option, falling back to search results if needed
 */
const extractGroupDisplayName = (groupOption: GroupOption, groupResults: GroupUMS[]): string => {
  const groupName = "value" in groupOption && typeof groupOption.value === "string" ? groupOption.value : groupOption.name;

  if (!groupOption.name && !groupOption.description && groupName) {
    const foundGroup = groupResults.find((group) => group.name === groupName);
    return foundGroup?.name || groupName;
  }

  return (
    groupOption.name ||
    ("value" in groupOption && typeof groupOption.value === "string" ? groupOption.value : "") ||
    ("name" in groupOption && typeof groupOption.name === "string" ? groupOption.name : "")
  );
};

/**
 * Creates a reusable chip popover component for users or groups
 * @param chipElement - The chip element to attach the popover to
 * @param option - The user or group option data
 * @param searchResults - Array of search results to fall back to if option is incomplete
 * @param type - Whether this is for a "user" or "group"
 * @returns A React element containing the popover-wrapped chip
 */
function ChipPopover<T extends MultiOption>({
  children,
  type,
  searchResults,
  option,
}: Omit<ChipPopoverProps<T>, "option"> &
  ({ type: "user"; searchResults: UserUMS[]; option: UserOption } | { type: "group"; searchResults: GroupUMS[]; option: GroupOption })) {
  // Extract display name based on type
  const displayName = type === "user" ? extractUserDisplayName(option, searchResults) : extractGroupDisplayName(option, searchResults);

  const config = getPopoverConfig(type, displayName);

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover
      trigger="hover"
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement="top"
      closeOnBlur={false}
      isLazy
      gutter={POPOVER_GUTTER}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: POPOVER_OFFSET,
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
            padding: POPOVER_PADDING,
          },
        },
        {
          name: "zIndex",
          enabled: true,
          phase: "write",
          fn: ({ state }: { state: { styles?: { popper?: { zIndex?: number } } } }) => {
            if (state.styles?.popper) {
              state.styles.popper.zIndex = POPOVER_Z_INDEX;
            }
          },
        },
      ]}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          role="tooltip"
          aria-label={config.type === "user" ? "User details" : "Group details"}
          boxShadow="md"
          bg="neutrals.10"
          borderRadius="md"
          p={0}
          maxWidth="300px"
          width="auto"
          zIndex={POPOVER_Z_INDEX}
        >
          <PopoverArrow bg="neutrals.500" boxShadow="md" />
          <ChakraBox
            padding="8px 12px"
            minWidth="200px"
            maxWidth="300px"
            style={{ wordWrap: "break-word", overflowWrap: "break-word", borderRadius: "6px", overflow: "hidden" }}
            position="relative"
            bg="neutrals.500"
          >
            {config.displayName && (
              <ChakraBox marginBottom="4px" paddingRight="20px">
                <Text fontSize="xs" fontWeight="bold" color="white" marginBottom="2px">
                  {config.label}
                </Text>
                <Link
                  to={config.linkUrl}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsOpen(false);
                  }}
                  target="_blank"
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    color: "white",
                    display: "inline-block",
                  }}
                >
                  <Text fontSize="sm" color="white">
                    {config.displayName}
                  </Text>
                </Link>
              </ChakraBox>
            )}
          </ChakraBox>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

export function userChipPopover(searchResults: UserUMS[]): React.FC<ChipPopoverProps<UserOption>> {
  return function ({ children, option, index }: ChipPopoverProps<UserOption>) {
    return (
      <ChipPopover type="user" index={index} option={option} searchResults={searchResults}>
        {children}
      </ChipPopover>
    );
  };
}

export function groupChipPopover(searchResults: GroupUMS[]): React.FC<ChipPopoverProps<GroupOption>> {
  return function ({ children, option, index }: ChipPopoverProps<GroupOption>) {
    return (
      <ChipPopover type="group" index={index} option={option} searchResults={searchResults}>
        {children}
      </ChipPopover>
    );
  };
}
